/*
 *
 * File: utils.js
 *
 * Purpose: Common utility functions.
 *
 *          Following exported functions:
 *            - timeConvert
 *            - getCarCategoryData
 *            - getTariffs
 * 
 * Autor:   Thomas Burgard (Software development)
 * Created: 16.03.2022
 *
 * Copyright (c) DrivePartner GmbH 2022
 *
 */

import { usrCalcRouteObj, usrCarCategoryObj, usrRegistrationDataObj } from "./dataObjects";
import * as constants from '../utils/constants';

/**
 * Convert minutes into hours and minutes.
 *
 * @param {*} min minutes to convert in neccessary format (xx h yy min)
 * @returns converted time -> xx h yy min
 */
export const timeConvert = (min) => {
  const num = min;
  const hours = num / 60;
  const rhours = Math.floor(hours);
  const minutes = (hours - rhours) * 60;
  const rminutes = Math.round(minutes);

  return rhours + " h " + rminutes + " min";
};

/**
 * Get user car category data and store in car category data object.
 * 
 * @param {*} idx idx Car type array index
 * @param {*} userData user data from server 
 * @returns usrCarCategoryObj user car category object
 */
 export const getCarCategoryData = (idx, userData) => {     
  if (userData.driverCars[idx]["PRICE"] === null || userData.driverCars[idx]["PRICE"] === "") {
    usrCarCategoryObj.fareDistArea1 = 0;
  } else {
    usrCarCategoryObj.fareDistArea1 = parseFloat(userData.driverCars[idx]["PRICE"]);
  } 

  if (userData.driverCars[idx]["STARTPRICE"] === null || userData.driverCars[idx]["STARTPRICE"] === "") {
    usrCarCategoryObj.startFare = 0;
  } else {
    usrCarCategoryObj.startFare = parseFloat(userData.driverCars[idx]["STARTPRICE"]);
  } 
   
  if (userData.driverCars[idx]["FREEKM"] === null || userData.driverCars[idx]["FREEKM"] === "") {
    usrCarCategoryObj.freeKm = 0;
  } else {
    usrCarCategoryObj.freeKm = parseInt(userData.driverCars[idx]["FREEKM"]);
  }    
   
  if (userData.driverCars[idx]["DISTAREA2"] === null || userData.driverCars[idx]["DISTAREA2"] === "") {
    usrCarCategoryObj.distArea2 = 0;
  } else {
    usrCarCategoryObj.distArea2 = parseInt(userData.driverCars[idx]["DISTAREA2"]);
  } 
 
  if (userData.driverCars[idx]["DISTAREA3"] === null || userData.driverCars[idx]["DISTAREA3"] === "") {
    usrCarCategoryObj.distArea3 = 0;
  } else {
    usrCarCategoryObj.distArea3 = parseInt(userData.driverCars[idx]["DISTAREA3"]);
  } 
  
  if (userData.driverCars[idx]["PRICE_DISTAREA2"] === null || userData.driverCars[idx]["PRICE_DISTAREA2"] === "") {
    usrCarCategoryObj.fareDistArea2 = 0;
  } else {
    usrCarCategoryObj.fareDistArea2 = parseFloat(userData.driverCars[idx]["PRICE_DISTAREA2"]);
  } 
  
  if (userData.driverCars[idx]["PRICE_DISTAREA3"] === null || userData.driverCars[idx]["PRICE_DISTAREA3"] === "") {
    usrCarCategoryObj.fareDistArea3 = 0;
  } else {
    usrCarCategoryObj.fareDistArea3 = parseFloat(userData.driverCars[idx]["PRICE_DISTAREA3"]);
  } 
  
  usrCarCategoryObj.carCategory = userData.driverCars[idx]["CARCATEGORY"];
  usrCarCategoryObj.carModel1 = userData.driverCars[idx]["MODEL1"];
  usrCarCategoryObj.carModel2 = userData.driverCars[idx]["MODEL2"];
  usrCarCategoryObj.carModel3 = userData.driverCars[idx]["MODEL3"];
  usrCarCategoryObj.carModel4 = userData.driverCars[idx]["MODEL4"];
  
  if (userData.driverCars[idx]["HOURPRICE"] === null || userData.driverCars[idx]["HOURPRICE"] === "") {
    usrCarCategoryObj.hourFare = 0;
  } else {
    usrCarCategoryObj.hourFare = parseFloat(userData.driverCars[idx]["HOURPRICE"]);
  } 
  
  if (userData.driverCars[idx]["HOURFREEKM"] === null || userData.driverCars[idx]["HOURFREEKM"] === "") {
    usrCarCategoryObj.hourFreeKm = 0;
  } else {
    usrCarCategoryObj.hourFreeKm = userData.driverCars[idx]["HOURFREEKM"];
  } 

  return usrCarCategoryObj;
}; 

/**
   * Get tariff values from server data (table "tblAddTariffs").
   * 
   *   - premium/standard tariff
   *   - shortest/fastest route
   *   - round up/off not round up/off price
   * 
   * @param {*} userData user data from server 
   * @returns usrCalcRouteObj
   */
 export const getTariffs = (userData) => {     
  if (userData.tariffs !== false) {
    usrCalcRouteObj.tariffsConfigured = true;

    for (let idx = 0; idx < userData.tariffs.length; idx++) {
      if (userData.tariffs[idx]["Name"] === constants.PACKAGE_NAME_STRECKENPREISOPTIMIERUNG) {
        usrCalcRouteObj.routePriceOptimization = true;

        if (userData.tariffs[idx]["Func1"] === "1") { // Function 1 = Route with different tariffs
          usrCalcRouteObj.routeWithDifferentTariffs = true;
        } 
        
        if (userData.tariffs[idx]["Func2"] === "1") { // Function 2 = Specify route selection (shortest or fastest)
          // Do nothing in the moment!
        }  

        if (userData.tariffs[idx]["Func3"] === "1") { // Function 3 = "Round up/off route prices
          // Do nothing in the moment!
        }
      } else if (userData.tariffs[idx]["Name"] === constants.SUBFEATURE_NAME_STRECKENAUSWAHL) {
        if (userData.tariffs[idx]["Func7"] === constants.VALUE_STRECKENAUSWAHL_KUERZESTE) {
          usrCalcRouteObj.shortestRoute = true;
          usrCalcRouteObj.fastestRoute = false;
        } else if (userData.tariffs[idx]["Func7"] === constants.VALUE_STRECKENAUSWAHL_SCHMNELLSTE) {
          usrCalcRouteObj.fastestRoute = true; 
          usrCalcRouteObj.shortestRoute = false;
          usrCalcRouteObj.maxKmLongerAsShortestRoute = parseInt(userData.tariffs[idx]["Func15"]);
        }
      } else if (userData.tariffs[idx]["Name"] === constants.SUBFEATURE_NAME_RUNDUNG_STRECKENPREISE) {
        if (userData.tariffs[idx]["Func7"] === constants.VALUE_RUNDUNG_STRECKENPREISE_NICHT_RUNDEN) {
          usrCalcRouteObj.roundNotRoutePrices = true;
        } else if (userData.tariffs[idx]["Func7"] === constants.VALUE_RUNDUNG_STRECKENPREISE_AUFRUNDEN) {
          usrCalcRouteObj.roundUpRoutePrices = true;
        } else if (userData.tariffs[idx]["Func7"] === constants.VALUE_RUNDUNG_STRECKENPREISE_ABRUNDEN) {
          usrCalcRouteObj.roundOffRoutePrices = true;
        }    
      }
    } // End loop     
  } else {
    usrCalcRouteObj.tariffsConfigured = false;
  }

  return usrCalcRouteObj;
};

/**
  * Get user registration values from server data (table "tblRegistration").
  * * 
  * @param {*} userData user data from server 
  * @returns usrRegistrationDataObj
  */
 export const getUserRegistrationData = (userData) => {    
  usrRegistrationDataObj.userID = userData.regData.UserID;
  usrRegistrationDataObj.username = userData.regData.BenutzerName;
  usrRegistrationDataObj.userPswd = userData.regData.Passwort;
  usrRegistrationDataObj.annahmefrist = userData.regData.AnnahmeFristen;
  usrRegistrationDataObj.companynameOrOwner = userData.regData.FIRMENNAMEODERINHABER;
  usrRegistrationDataObj.services = userData.regData.SERVICES;
  usrRegistrationDataObj.locations = userData.regData.LOCATIONS;
  usrRegistrationDataObj.tariff = parseInt(userData.regData.Tariff);
  usrRegistrationDataObj.showTariffRegulationText = parseInt(userData.regData.SHOWTEXTTARIFORDNUNG);
  usrRegistrationDataObj.agbLink = userData.regData.AGBREF;
  usrRegistrationDataObj.dataprotectionLink = userData.regData.DATENSCHUTZREF;
  usrRegistrationDataObj.impressumFirmenName = userData.regData.FirmaName;
  usrRegistrationDataObj.impressumFirmenStrasse = userData.regData.FirmaStrasse;
  usrRegistrationDataObj.impressumFirmenPlz = userData.regData.FirmaPlz;
  usrRegistrationDataObj.impressumFirmenStadt = userData.regData.FirmaStadt;
  usrRegistrationDataObj.impressumFirmenLand = userData.regData.FirmaLand;
  usrRegistrationDataObj.impressumFirmenUstId = userData.regData.FirmaUmsatzsteuerId;
  usrRegistrationDataObj.impressumFirmenWebAdresse = userData.regData.FirmaInternetAdresse;
  usrRegistrationDataObj.impressumFirmenTelefon = userData.regData.ImpressumTel;
  usrRegistrationDataObj.impressumFirmenMail = userData.regData.ImpressumEmail;
  usrRegistrationDataObj.impressumRegisterGericht = userData.regData.ImpressumRegistergericht;
  usrRegistrationDataObj.impressumHRB = userData.regData.ImpressumRegisternummer;
  usrRegistrationDataObj.impressumRechtsform = userData.regData.ImpressumRechtsform;
  usrRegistrationDataObj.impressumGF = userData.regData.ImpressumGF;
  usrRegistrationDataObj.stripeConnectID = userData.regData.STRIPECONNECTID;

  if (userData.regData.Tariff === 2) {
    usrRegistrationDataObj.premium = "YES_PREMIUMBOOKING";
  }

  return usrRegistrationDataObj;
 } 

/**
 * Round up/off or not round dependent on user tariff configuration.
 * 
 * @param {*} fare given fare for round or not round
 * @param {*} userCalcRouteObj user calculation route object
 * @returns rounded or not rounded fare value
 */
export const roundFare = (fare, userCalcRouteObj) => {
  let locFare = fare; 
  
  if (userCalcRouteObj.routePriceOptimization) {
    if (!userCalcRouteObj.roundNotRoutePrices) {
      if (userCalcRouteObj.roundUpRoutePrices) {
        let lastDigit = locFare % 10;

        if (lastDigit > 0) {
          if (lastDigit > 5) {
            locFare = locFare + (10 - lastDigit);
          } else if (lastDigit < 5) {
            locFare = locFare + (5 - lastDigit);
          }
        }
      } else if (userCalcRouteObj.roundOffRoutePrices) {
        let lastDigit = locFare % 10;

        if (lastDigit > 0) {
          if (lastDigit > 5) {
            locFare = locFare - (lastDigit - 5);
          } else if (lastDigit < 5) {
            locFare = locFare - lastDigit;
          }
        } 
      }  
    }  
  } 

  return locFare;
};

/**
 * Convert date from "yyy-mm-dd" to german "dd.mm.yyyy"
 * 
 * @param {*} dateString Date string to convert into german format
 */
export const germanDate = (dateString) => {
  let germanDate = dateString.split("-");
  germanDate = germanDate[2] + "." + germanDate[1] + "." + germanDate[0];

  return germanDate;   
};

/**
 * Fetch username from given browser url.
 *
 * @param {*} url given browser url
 * @returns username
 */
export const getUserNameFromURL = (url) => {
  let strHttps = url.substring(0, 12);
  let strHttp = url.substring(0, 11);
  let strWithoutWWW = url.substring(0, 8);

  if (strHttp === "http://www.") {
    return strHttp === "http://www."
      ? url.replace("http://www.", "").split(".")[0]
      : url.replace("http://", "").split(".")[0];
  } else if (strHttps === "https://www.") {
    return strHttps === "https://www."
      ? url.replace("https://www.", "").split(".")[0]
      : url.replace("https://", "").split(".")[0];
  } else if (strWithoutWWW === "https://") {
    return url.replace("https://", "").split(".")[0];
  }
};
